import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import moment from 'moment';
import Constants from '@/utils/constants';

/**
 * Common mixin functionality converted to a composable
 * Provides methods previously available through commonMixin
 */
export function useCommonMixin() {
  const { t } = useI18n();
  const store = useStore();

  const calculateDateTimeOffsetAndPoll = (dateTimeNow, conferenceCallStartDateTime, conferenceCallEnableLiveStream, isAccessRevoked) => {
    const conferenceCallStartTime = moment(conferenceCallStartDateTime).local(); //converting UTC time to local
    
    const datetimeFromServerMoment = moment(dateTimeNow);
    
    //check if the start time has passed or is the same
    if (datetimeFromServerMoment.isAfter(conferenceCallStartTime)) {
    
        //In this case stop time api and call the conference call status Api
        store.dispatch('conferenceCall/startConferenceCallStatusPolling');
        store.dispatch('conferenceCall/startUserStatusPolling');
        store.dispatch('conferenceCall/fetchDateTimeNow', true);
        store.dispatch('conferenceCall/setHasConferenceStarted', true);
    } else {
    
        segmentDateIntoDuration(datetimeFromServerMoment, conferenceCallStartTime, isAccessRevoked);
        store.dispatch('conferenceCall/setHasConferenceStarted', false);
    }
  };

  const onStatusChanged = (conferenceCallStatus, LiveStreamEnabled, userStatus, liveStreamUrl, isAccessRevoked, isPublicParticipant) => {
    let statusString = null;

    if (conferenceCallStatus.Status === Constants.ConferenceStatus.Expired) {
        statusString = t(Constants.ConferenceCallStringStatus.Expired)
        store.dispatch('conferenceCall/stopConferenceCallStatusPolling');
        store.dispatch('conferenceCall/stopUserStatusPolling');
    }
    
    if(conferenceCallStatus.Status === Constants.ConferenceStatus.Ended) {
        statusString = getEndedStatus(conferenceCallStatus.StreamingState);
        store.dispatch('conferenceCall/slowCallStatusPolling');
        store.dispatch('conferenceCall/stopUserStatusPolling');
    }

    if (conferenceCallStatus.Status === Constants.ConferenceStatus.Available) {
        if (LiveStreamEnabled) {
            if(liveStreamUrl !== null) {
                statusString = t(Constants.ConferenceCallStringStatus.StandBy);
            }
            else {
                statusString = t(Constants.ConferenceCallStringStatus.AvailableWithStreaming);
            }
            store.dispatch('conferenceCall/setAssistanceMessage', true);
        }
        else {
            statusString = t(Constants.ConferenceCallStringStatus.AvailableWithoutStreaming)
        }        
    }

    if (userStatus && userStatus === "Connected" && conferenceCallStatus.Status === Constants.ConferenceStatus.Available) {
        statusString = t(Constants.ConferenceCallStringStatus.StandBy);
        store.dispatch('conferenceCall/setAssistanceMessage', true);
    }

    if (conferenceCallStatus.Status === Constants.ConferenceStatus.Live) {
        if (userStatus && userStatus === "Connected") {
            statusString = t(Constants.ConferenceCallStringStatus.LiveUserConnected);
            store.dispatch('conferenceCall/setAssistanceMessage', true);
        }
        else {
            if (LiveStreamEnabled) {
                if(liveStreamUrl !== null) {
                    statusString = t(Constants.ConferenceCallStringStatus.LiveUserConnected);
                }
                else {
                    statusString = t(Constants.ConferenceCallStringStatus.LiveStreamingAvailableUserNotConnected);
                }
                store.dispatch('conferenceCall/setAssistanceMessage', true);
            }
            else {
                statusString = t(Constants.ConferenceCallStringStatus.LiveStreamingUnavailableUserNotConnected)
            }
        }        
    }

    if (isAccessRevoked) {
        let callEnded = conferenceCallStatus.Status === Constants.ConferenceStatus.Ended || conferenceCallStatus.Status === Constants.ConferenceStatus.Expired;
        
        // Show access revoked status when individual access is revoked or when public access is revoked and call has not ended.
        if (!isPublicParticipant || (isPublicParticipant && conferenceCallStatus.PublicCallEndTimeUtc && !callEnded)) {
            statusString = t(Constants.ConferenceCallStringStatus.ParticipantAccessRevoked);
        }
    }

    //Call action to update state
    if (!!statusString) {
        store.dispatch('conferenceCall/setCallStatusOnPage', statusString)
    }
  };

  const getEndedStatus = (StreamingState) => {
    if(StreamingState === Constants.StreamingStates.Published) {
      return t(Constants.ConferenceCallStringStatus.CallEndedReplayReady);
    }
    return t(Constants.ConferenceCallStringStatus.CallEndedReplayNotReady);
  };

  const segmentDateIntoDuration = (dateTimeNow, cCStartTime, isAccessRevoked) => {
    let offsetObject = {};

    //Calculate duration after calculating differnce between these dates
    offsetObject = moment.duration(cCStartTime.diff(dateTimeNow))._data;

    //Converting Duration if years or months are left
    if (offsetObject.years) {
        offsetObject.days += moment.duration(offsetObject.years, 'years').asDays();
        if (offsetObject.months) {
            offsetObject.days += moment.duration(offsetObject.months, 'months').asDays();
        }
    }
    else if (offsetObject.months) {
        offsetObject.days += moment.duration(offsetObject.months, 'months').asDays();
    }

    //Rounding Up
    if (offsetObject.days) {
        // >0 day
        if (offsetObject.hours >= 0 && offsetObject.minutes) {
            //>=0 hours && >0min
            offsetObject.hours += 1;
            if (offsetObject.hours === 24) {
                offsetObject.hours = 0;
                offsetObject.days += 1;
            }
            offsetObject.minutes = 0;
        }

    }
    else if (offsetObject.hours) {
        //>0 hrs
        if (offsetObject.seconds) {
            // > 0secs and 0 mins
            offsetObject.minutes += 1;
            offsetObject.seconds = 0;
        }
    }
    else if (offsetObject.minutes) {
        if (offsetObject.seconds > 0) {
            offsetObject.minutes += 1;
        }
    }
    else {
        offsetObject.minutes = 1;
        offsetObject.seconds = 0;
    }

    createCallStatusIfTimeLeft(offsetObject, isAccessRevoked);
  };

  const createCallStatusIfTimeLeft = (offsetObject, isAccessRevoked) => {
    let timeLeft = createTimeLeftString(offsetObject)
    let status = null;

    if (!offsetObject.days && !offsetObject.hours && offsetObject.minutes <= 10) {

        status = t(Constants.ConferenceCallStringStatus.TimeLeftWithInTenMinutes, {
            totalMinutes : offsetObject.minutes,
            minutesSuffix: offsetObject.minutes > 1 ? t('date_time_unit.minutes') : t('date_time_unit.minute'),});
        store.dispatch('conferenceCall/setAssistanceMessage', true);
    }
    else {
        status = timeLeft;
        store.dispatch('conferenceCall/setAssistanceMessage', false);
    }
    
    if(isAccessRevoked){
        status = t(Constants.ConferenceCallStringStatus.ParticipantAccessRevoked);
    }

    if (status) {
        store.dispatch('conferenceCall/setCallStatusOnPage', status)
    }
  };

  const createTimeLeftString = (offsetObject) => {
    let timeLeftFromCCStart = '';

    if (offsetObject.days) {
        timeLeftFromCCStart = t(Constants.ConferenceCallStringStatus.TimeLeftString, {
            days: offsetObject.days,
            daysSuffix: offsetObject.days > 1 ? t('date_time_unit.days') : t('date_time_unit.day'),
        });
    }

    if (offsetObject.hours) {
        if (offsetObject.days) {
            timeLeftFromCCStart = t(Constants.ConferenceCallStringStatus.TimeLeftString, {
                days: offsetObject.days, daysSuffix: offsetObject.days > 1 ? t('date_time_unit.days') : t('date_time_unit.day'),
                hoursPrefix: t('date_time_unit.and'),
                hours: offsetObject.hours, hoursSuffix: offsetObject.hours > 1 ? t('date_time_unit.hours') : t('date_time_unit.hour'),
            });
        }
        else {
            timeLeftFromCCStart = t(Constants.ConferenceCallStringStatus.TimeLeftString, {
                hours: offsetObject.hours, hoursSuffix: offsetObject.hours > 1 ? t('date_time_unit.hours') : t('date_time_unit.hour'),
            });
        }
    }

    if (offsetObject.minutes) {
        if (offsetObject.days && !offsetObject.hours) {
            timeLeftFromCCStart = t(Constants.ConferenceCallStringStatus.TimeLeftString, {
                days: offsetObject.days, daysSuffix: offsetObject.days > 1 ? t('date_time_unit.days') : t('date_time_unit.day'),
                minutesPrefix: t('date_time_unit.and'), minutes: offsetObject.minutes,
                minutesSuffix: offsetObject.minutes > 1 ? t('date_time_unit.minutes') : t('date_time_unit.minute'),
            });
        }
        else if (!offsetObject.days && offsetObject.hours) {
            timeLeftFromCCStart = t(Constants.ConferenceCallStringStatus.TimeLeftString, {
                hours: offsetObject.hours, hoursSuffix: offsetObject.hours > 1 ? t('date_time_unit.hours') : t('date_time_unit.hour'),
                minutesPrefix: t('date_time_unit.and'), minutes: offsetObject.minutes,
                minutesSuffix: offsetObject.minutes > 1 ? t('date_time_unit.minutes') : t('date_time_unit.minute'),
            });
        }
        else if (offsetObject.days && offsetObject.hours) {
            timeLeftFromCCStart = t(Constants.ConferenceCallStringStatus.TimeLeftString, {
                days: offsetObject.days, daysSuffix: offsetObject.days > 1 ? t('date_time_unit.days') : t('date_time_unit.day'),
                hoursPrefix: t('date_time_unit.and'),
                hours: offsetObject.hours, hoursSuffix: offsetObject.hours > 1 ? t('date_time_unit.hours') : t('date_time_unit.hour'),
                minutesPrefix: t('date_time_unit.and'),
                minutes: offsetObject.minutes, minutesSuffix: offsetObject.minutes > 1 ? t('date_time_unit.minutes') : t('date_time_unit.minute')});
        }
        else {
            timeLeftFromCCStart = t(Constants.ConferenceCallStringStatus.TimeLeftString, {
                minutes: offsetObject.minutes, 
                minutesSuffix: offsetObject.minutes > 1 ? t('date_time_unit.minutes') : t('date_time_unit.minute'),
            });
        }
    }
    return timeLeftFromCCStart;
  };

  const findPhoneNumberObjectByCountry = (numbers, countryId) => {
    return numbers.find((number)=> {
      return number.Country.Id === countryId;  
    });
  };

  const isEditNotAllowedError = (errorMsg) => {
    const {ErrorEditingQuestion, DeletingTextQuestionErrorMessage} = Constants.QuestionErrorMsg;
    if (errorMsg === t(ErrorEditingQuestion) || errorMsg === t(DeletingTextQuestionErrorMessage)) {
        return true;
    }
    return false; 
  };

  return {
    calculateDateTimeOffsetAndPoll,
    onStatusChanged,
    getEndedStatus,
    segmentDateIntoDuration,
    createCallStatusIfTimeLeft,
    createTimeLeftString,
    findPhoneNumberObjectByCountry,
    isEditNotAllowedError
  };
}
